import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'dw_cart_timer_end';
const DURATION_MS = 10 * 60 * 1000;
const STYLE_ID = 'dw-cart-timer-styles';
const GLOBAL_SLOT_ID = 'dw-cart-timer-global';

// Non-dismissible cart countdown bar. Two places render their own static
// shell server-side (snippets/cart-timer-bar.liquid, included from
// snippets/cart-drawer.liquid and sections/main-cart.liquid) so the bar
// survives Shopify's own section-rendering morphs there and automatically
// shows/hides itself as the cart's contents change. This script's only job
// is to (1) keep every `.dw-cart-timer__display` element in the DOM in sync
// with a single countdown, wherever it happens to live, and (2) manage the
// one slot it truly owns: a global, site-wide bar above the header, shown
// only when neither the drawer nor the cart page already has its own copy
// visible — so the customer never sees two bars at once.
class CartCountdownTimer {
  /** @type {CartCountdownTimer | null} */
  static instance = null;

  static getInstance() {
    if (!CartCountdownTimer.instance) {
      CartCountdownTimer.instance = new CartCountdownTimer();
    }
    return CartCountdownTimer.instance;
  }

  constructor() {
    this.endTime = null;
    this.intervalId = null;
    this.isExpiring = false;
    this.drawerOpen = false;

    this.injectStyles();
    this.restore();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
    document.addEventListener('theme-drawer:open', this.handleDrawerToggle);
    document.addEventListener('theme-drawer:close', this.handleDrawerToggle);
  }

  // Reacts to every cart change (add, remove, update quantity) using the
  // real cart state Shopify returns, instead of only listening for 'add'.
  // This keeps the timer honest: if the cart actually becomes empty, the
  // countdown stops immediately rather than continuing to claim a
  // countdown that no longer applies to anything. Ignored while we're
  // mid-expiry (see expire()) since that flow dispatches its own cart
  // update event.
  handleCartLinesUpdate = (event) => {
    if (!event.promise || this.isExpiring) return;

    event.promise
      .then((result) => {
        if (result?.detail?.didError) return;

        const quantity = result?.cart?.totalQuantity ?? 0;

        if (quantity <= 0) {
          this.stop();
          return;
        }

        if (event.action === 'add') {
          this.start();
        } else if (this.endTime) {
          // Quantity change or partial removal that still leaves items in
          // the cart: keep the existing countdown running, just make sure
          // the global slot reflects the current context.
          this.syncGlobalSlot();
        }
      })
      .catch(() => {});
  };

  // theme-drawer:open/close fire for ANY drawer (search, cart, etc.), so we
  // re-derive state directly from the cart drawer's own [open] attribute
  // rather than trusting the event shape.
  handleDrawerToggle = () => {
    const drawer = document.getElementById('cart-drawer');
    this.drawerOpen = Boolean(drawer && drawer.hasAttribute('open'));
    this.syncGlobalSlot();
  };

  isOnCartPage() {
    return document.getElementById('MainContent')?.dataset.template === 'cart';
  }

  start() {
    this.endTime = Date.now() + DURATION_MS;
    this.persist();
    this.syncGlobalSlot();
    this.tick();

    this.clearIntervalId();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  restore() {
    let stored = null;
    try {
      stored = Number(localStorage.getItem(STORAGE_KEY));
    } catch (e) {
      stored = null;
    }

    if (!stored || stored <= Date.now()) return;

    this.endTime = stored;
    this.syncGlobalSlot();
    this.tick();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  persist() {
    try {
      localStorage.setItem(STORAGE_KEY, String(this.endTime));
    } catch (e) {
      // localStorage unavailable or full — timer still works for this page view
    }
  }

  clearIntervalId() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  formattedTime() {
    const remaining = Math.max(0, this.endTime - Date.now());
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  tick() {
    if (!this.endTime) return;
    const remaining = this.endTime - Date.now();

    if (remaining <= 0) {
      this.expire();
      return;
    }

    const text = this.formattedTime();
    document.querySelectorAll('.dw-cart-timer__display').forEach((el) => {
      el.textContent = text;
    });
  }

  // Countdown hit zero: actually empty the cart via Shopify's own endpoint
  // (no fake "expired" state with nothing behind it), then tell the rest of
  // the theme (cart icon, cart drawer, cart page) so they update without a
  // refresh — which also makes every server-rendered timer-bar copy
  // disappear on its own via the `unless cart.empty?` guard.
  async expire() {
    this.isExpiring = true;
    this.clearIntervalId();
    this.endTime = null;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }

    document.querySelectorAll('.dw-cart-timer').forEach((bar) => {
      const display = bar.querySelector('.dw-cart-timer__display');
      if (display) display.textContent = '0:00';
      const label = bar.querySelector('.dw-cart-timer__label');
      if (label) label.textContent = 'Winkelwagen is geleegd';
    });

    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const cart = await response.json();
        this.notifyCartCleared(cart);
      }
    } catch (e) {
      // Network error — cart stays as-is. Fail safe: don't claim the cart
      // was emptied when nothing actually changed.
    }

    setTimeout(() => {
      this.removeGlobalSlot();
      this.isExpiring = false;
    }, 3000);
  }

  // Dispatches a real CartLinesUpdateEvent so other cart-aware theme
  // components (cart icon count, cart drawer, cart page) sync to the now-
  // empty cart the same way they would for any other cart change.
  notifyCartCleared(cart) {
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'dw-cart-countdown-timer',
      },
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );
  }

  stop() {
    this.clearIntervalId();
    this.endTime = null;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }

    this.removeGlobalSlot();
  }

  // The drawer and cart page each render their own timer markup server-side
  // (snippets/cart-timer-bar.liquid) — this method only manages the global,
  // site-wide bar prepended above the header, which this script fully owns.
  // It's intentionally suppressed on the cart page and while the drawer is
  // open, since those already show their own copy: the customer should
  // never see two bars at once.
  syncGlobalSlot() {
    if (!this.endTime) {
      this.removeGlobalSlot();
      return;
    }

    if (this.isOnCartPage() || this.drawerOpen) {
      this.removeGlobalSlot();
      return;
    }

    let el = document.getElementById(GLOBAL_SLOT_ID);
    if (!el) {
      el = this.buildBarElement();
      document.body.insertBefore(el, document.body.firstChild);
    }

    const display = el.querySelector('.dw-cart-timer__display');
    if (display) display.textContent = this.formattedTime();
  }

  removeGlobalSlot() {
    document.getElementById(GLOBAL_SLOT_ID)?.remove();
  }

  buildBarElement() {
    const el = document.createElement('div');
    el.id = GLOBAL_SLOT_ID;
    el.className = 'dw-cart-timer';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = `
      <span class="dw-cart-timer__inner">
        <span class="dw-cart-timer__icon" aria-hidden="true">&#9201;</span>
        <span class="dw-cart-timer__text">
          <span class="dw-cart-timer__label">JE BESTELLING IS GERESERVEERD VOOR</span>
          <span class="dw-cart-timer__display">${this.formattedTime()}</span>
        </span>
      </span>
    `;
    return el;
  }

  injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .dw-cart-timer {
        position: relative;
        z-index: 30;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 100%;
        box-sizing: border-box;
        background: #2F4035;
        color: #F7F3EC;
        font-family: 'DM Sans', sans-serif;
        padding: 10px 16px;
        box-shadow: 0 2px 10px rgba(28, 26, 24, 0.15);
        animation: dw-cart-timer-in 0.25s ease-out;
      }

      .dw-cart-timer__inner {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        justify-content: center;
      }

      .dw-cart-timer__icon {
        font-size: 1.1rem;
        line-height: 1;
        flex-shrink: 0;
      }

      .dw-cart-timer__text {
        display: flex;
        align-items: baseline;
        gap: 6px;
        line-height: 1.2;
      }

      .dw-cart-timer__label {
        font-size: 0.8rem;
        opacity: 0.9;
        letter-spacing: 0.02em;
      }

      .dw-cart-timer__display {
        font-size: 0.9rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: #B89150;
      }

      @keyframes dw-cart-timer-in {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @media (max-width: 640px) {
        .dw-cart-timer {
          padding: 8px 12px;
          font-size: 0.85rem;
        }

        .dw-cart-timer__label {
          font-size: 0.72rem;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .dw-cart-timer {
          animation: none;
        }
      }
    `;

    document.head.appendChild(style);
  }
}

CartCountdownTimer.getInstance();
